import type { BuiltInLanguage, OptionalCategory } from './types';

/**
 * Known third-party trackers: how to recognise them (script / iframe URLs), what category they belong to,
 * which cookies they set and what for. Single source of truth for auto-blocking, the banner's categories,
 * cookie erasing on reject and the cookie tables.
 *
 * Durations are as documented by each provider (verify when the provider changes them).
 */

export type TrackerId =
  | 'ga4'
  | 'gtm'
  | 'googleAds'
  | 'metaPixel'
  | 'hotjar'
  | 'clarity'
  | 'linkedinInsight'
  | 'tiktokPixel'
  | 'youtube'
  | 'vimeo'
  | 'googleMaps';

export type DurationUnit = 'minute' | 'day' | 'month' | 'year';
/** `null` = session cookie. */
export type Duration = { value: number; unit: DurationUnit } | null;

export interface TrackerCookie {
  /** Exact name, or a pattern for names with a suffix (e.g. `_ga_<container id>`). */
  name: string | RegExp;
  /** Name as shown in tables, e.g. `_ga_*`. */
  display: string;
  duration: Duration;
  /** Set on the site's own domain (so it can be seen and erased) or by the provider's domain. */
  firstParty: boolean;
}

export interface Tracker {
  id: TrackerId;
  label: string;
  provider: string;
  category: OptionalCategory;
  /** Google tag: loaded and governed by Consent Mode in `googleMode: 'advanced'`, blocked in `'basic'`. */
  google?: boolean;
  /** Never blocked (a tag loader such as GTM decides per tag through Consent Mode). */
  neverBlock?: boolean;
  scripts: RegExp[];
  iframes: RegExp[];
  cookies: TrackerCookie[];
  purpose: Record<BuiltInLanguage, string>;
  onAccept?: () => void;
  onReject?: () => void;
}

type AnyFn = (...args: unknown[]) => void;
const call = (name: string, ...args: unknown[]): void => {
  const fn = (window as unknown as Record<string, unknown>)[name];
  if (typeof fn === 'function') (fn as AnyFn)(...args);
};

const years = (value: number): Duration => ({ value, unit: 'year' });
const months = (value: number): Duration => ({ value, unit: 'month' });
const days = (value: number): Duration => ({ value, unit: 'day' });
const minutes = (value: number): Duration => ({ value, unit: 'minute' });

export const TRACKERS: readonly Tracker[] = [
  {
    id: 'ga4',
    label: 'Google Analytics',
    provider: 'Google',
    category: 'analytics',
    google: true,
    scripts: [/googletagmanager\.com\/gtag\/js\?(?:.*&)?id=(?:G|GT|UA)-/i, /google-analytics\.com\/(?:analytics|ga)\.js/i],
    iframes: [],
    cookies: [
      { name: '_ga', display: '_ga', duration: years(2), firstParty: true },
      { name: /^_ga_/, display: '_ga_*', duration: years(2), firstParty: true },
      { name: '_gid', display: '_gid', duration: days(1), firstParty: true },
    ],
    purpose: {
      en: 'Distinguishes visitors and visits to produce statistics on how the site is used.',
      sl: 'Razlikuje obiskovalce in obiske za statistiko uporabe strani.',
      de: 'Unterscheidet Besucher und Besuche, um Statistiken zur Nutzung der Website zu erstellen.',
      it: 'Distingue visitatori e visite per produrre statistiche sull’uso del sito.',
      hr: 'Razlikuje posjetitelje i posjete radi statistike korištenja stranice.',
    },
  },
  {
    id: 'gtm',
    label: 'Google Tag Manager',
    provider: 'Google',
    category: 'analytics',
    google: true,
    neverBlock: true,
    scripts: [/googletagmanager\.com\/gtm\.js/i],
    iframes: [/googletagmanager\.com\/ns\.html/i],
    cookies: [],
    purpose: {
      en: 'Loads the site’s tags. Sets no cookies itself; each tag follows your choice.',
      sl: 'Nalaga oznake strani. Sam ne nastavlja piškotkov; vsaka oznaka upošteva vašo izbiro.',
      de: 'Lädt die Tags der Website. Setzt selbst keine Cookies; jedes Tag folgt Ihrer Auswahl.',
      it: 'Carica i tag del sito. Non imposta cookie; ogni tag rispetta la tua scelta.',
      hr: 'Učitava oznake stranice. Sam ne postavlja kolačiće; svaka oznaka poštuje vaš izbor.',
    },
  },
  {
    id: 'googleAds',
    label: 'Google Ads',
    provider: 'Google',
    category: 'marketing',
    google: true,
    scripts: [
      /googletagmanager\.com\/gtag\/js\?(?:.*&)?id=(?:AW|DC)-/i,
      /googleadservices\.com\//i,
      /googlesyndication\.com\//i,
      /doubleclick\.net\//i,
    ],
    iframes: [/doubleclick\.net\//i],
    cookies: [{ name: /^_gcl_/, display: '_gcl_*', duration: months(3), firstParty: true }],
    purpose: {
      en: 'Measures which ads lead to visits and conversions and enables remarketing.',
      sl: 'Meri, kateri oglasi vodijo do obiskov in konverzij, in omogoča ponovno oglaševanje.',
      de: 'Misst, welche Anzeigen zu Besuchen und Conversions führen, und ermöglicht Remarketing.',
      it: 'Misura quali annunci portano a visite e conversioni e consente il remarketing.',
      hr: 'Mjeri koji oglasi dovode do posjeta i konverzija te omogućuje ponovno oglašavanje.',
    },
  },
  {
    id: 'metaPixel',
    label: 'Meta Pixel',
    provider: 'Meta',
    category: 'marketing',
    scripts: [/connect\.facebook\.net\/.*\/fbevents\.js/i, /connect\.facebook\.net\//i],
    iframes: [/facebook\.com\/tr/i],
    cookies: [
      { name: '_fbp', display: '_fbp', duration: months(3), firstParty: true },
      { name: '_fbc', display: '_fbc', duration: months(3), firstParty: true },
    ],
    purpose: {
      en: 'Measures ads on Facebook and Instagram and shows more relevant ads.',
      sl: 'Meri oglase na Facebooku in Instagramu ter prikazuje ustreznejše oglase.',
      de: 'Misst Anzeigen auf Facebook und Instagram und zeigt relevantere Anzeigen.',
      it: 'Misura gli annunci su Facebook e Instagram e mostra annunci più pertinenti.',
      hr: 'Mjeri oglase na Facebooku i Instagramu te prikazuje relevantnije oglase.',
    },
    onAccept: () => call('fbq', 'consent', 'grant'),
    onReject: () => call('fbq', 'consent', 'revoke'),
  },
  {
    id: 'hotjar',
    label: 'Hotjar',
    provider: 'Hotjar',
    category: 'analytics',
    scripts: [/static\.hotjar\.com\//i, /script\.hotjar\.com\//i],
    iframes: [],
    cookies: [
      { name: /^_hjSessionUser_/, display: '_hjSessionUser_*', duration: years(1), firstParty: true },
      { name: /^_hjSession_/, display: '_hjSession_*', duration: minutes(30), firstParty: true },
    ],
    purpose: {
      en: 'Records how visitors use pages (clicks, scrolling) to improve them.',
      sl: 'Beleži, kako obiskovalci uporabljajo strani (kliki, drsenje), da jih izboljšamo.',
      de: 'Erfasst, wie Besucher Seiten nutzen (Klicks, Scrollen), um sie zu verbessern.',
      it: 'Registra come i visitatori usano le pagine (clic, scorrimento) per migliorarle.',
      hr: 'Bilježi kako posjetitelji koriste stranice (klikovi, pomicanje) radi poboljšanja.',
    },
  },
  {
    id: 'clarity',
    label: 'Microsoft Clarity',
    provider: 'Microsoft',
    category: 'analytics',
    scripts: [/clarity\.ms\//i],
    iframes: [],
    cookies: [
      { name: '_clck', display: '_clck', duration: years(1), firstParty: true },
      { name: '_clsk', display: '_clsk', duration: days(1), firstParty: true },
    ],
    purpose: {
      en: 'Records how visitors use pages (clicks, scrolling) to improve them.',
      sl: 'Beleži, kako obiskovalci uporabljajo strani (kliki, drsenje), da jih izboljšamo.',
      de: 'Erfasst, wie Besucher Seiten nutzen (Klicks, Scrollen), um sie zu verbessern.',
      it: 'Registra come i visitatori usano le pagine (clic, scorrimento) per migliorarle.',
      hr: 'Bilježi kako posjetitelji koriste stranice (klikovi, pomicanje) radi poboljšanja.',
    },
    onAccept: () => call('clarity', 'consent', true),
    onReject: () => call('clarity', 'consent', false),
  },
  {
    id: 'linkedinInsight',
    label: 'LinkedIn Insight Tag',
    provider: 'LinkedIn',
    category: 'marketing',
    scripts: [/snap\.licdn\.com\//i, /px\.ads\.linkedin\.com\//i],
    iframes: [],
    cookies: [{ name: 'li_sugr', display: 'li_sugr', duration: months(3), firstParty: true }],
    purpose: {
      en: 'Measures LinkedIn ads and builds audiences for them.',
      sl: 'Meri oglase na LinkedInu in zanje gradi ciljne skupine.',
      de: 'Misst LinkedIn-Anzeigen und erstellt Zielgruppen dafür.',
      it: 'Misura gli annunci LinkedIn e crea pubblici per essi.',
      hr: 'Mjeri oglase na LinkedInu i za njih gradi ciljne skupine.',
    },
  },
  {
    id: 'tiktokPixel',
    label: 'TikTok Pixel',
    provider: 'TikTok',
    category: 'marketing',
    scripts: [/analytics\.tiktok\.com\//i],
    iframes: [],
    cookies: [{ name: '_ttp', display: '_ttp', duration: months(13), firstParty: true }],
    purpose: {
      en: 'Measures TikTok ads and shows more relevant ads.',
      sl: 'Meri oglase na TikToku in prikazuje ustreznejše oglase.',
      de: 'Misst TikTok-Anzeigen und zeigt relevantere Anzeigen.',
      it: 'Misura gli annunci TikTok e mostra annunci più pertinenti.',
      hr: 'Mjeri oglase na TikToku i prikazuje relevantnije oglase.',
    },
  },
  {
    id: 'youtube',
    label: 'YouTube',
    provider: 'Google',
    category: 'marketing',
    scripts: [/youtube\.com\/iframe_api/i, /youtube\.com\/s\/player/i],
    iframes: [/youtube(?:-nocookie)?\.com\/embed\//i],
    cookies: [
      { name: 'VISITOR_INFO1_LIVE', display: 'VISITOR_INFO1_LIVE', duration: months(6), firstParty: false },
      { name: 'YSC', display: 'YSC', duration: null, firstParty: false },
    ],
    purpose: {
      en: 'Plays embedded videos and may track viewing for advertising.',
      sl: 'Predvaja vgrajene videe in lahko spremlja ogledovanje za oglaševanje.',
      de: 'Spielt eingebettete Videos ab und kann das Ansehen für Werbung erfassen.',
      it: 'Riproduce i video incorporati e può tracciarne la visione a fini pubblicitari.',
      hr: 'Reproducira ugrađene videozapise i može pratiti gledanje radi oglašavanja.',
    },
  },
  {
    id: 'vimeo',
    label: 'Vimeo',
    provider: 'Vimeo',
    category: 'functional',
    scripts: [/player\.vimeo\.com\/api/i],
    iframes: [/player\.vimeo\.com\/video\//i],
    cookies: [{ name: 'vuid', display: 'vuid', duration: years(2), firstParty: false }],
    purpose: {
      en: 'Plays embedded videos and counts views.',
      sl: 'Predvaja vgrajene videe in šteje oglede.',
      de: 'Spielt eingebettete Videos ab und zählt Aufrufe.',
      it: 'Riproduce i video incorporati e ne conta le visualizzazioni.',
      hr: 'Reproducira ugrađene videozapise i broji preglede.',
    },
  },
  {
    id: 'googleMaps',
    label: 'Google Maps',
    provider: 'Google',
    category: 'functional',
    scripts: [/maps\.googleapis\.com\/maps\/api\/js/i],
    iframes: [/google\.[a-z.]+\/maps\/embed/i, /maps\.google\.[a-z.]+\//i],
    cookies: [{ name: 'NID', display: 'NID', duration: months(6), firstParty: false }],
    purpose: {
      en: 'Shows embedded maps and remembers map preferences.',
      sl: 'Prikazuje vgrajene zemljevide in si zapomni nastavitve zemljevida.',
      de: 'Zeigt eingebettete Karten an und merkt sich Karteneinstellungen.',
      it: 'Mostra le mappe incorporate e ricorda le preferenze della mappa.',
      hr: 'Prikazuje ugrađene karte i pamti postavke karte.',
    },
  },
];

const BY_ID = new Map<TrackerId, Tracker>(TRACKERS.map((t) => [t.id, t]));

export function trackerById(id: TrackerId): Tracker {
  const tracker = BY_ID.get(id);
  if (!tracker) throw new Error(`Unknown tracker ${id}`);
  return tracker;
}

export function isTrackerId(value: string): value is TrackerId {
  return BY_ID.has(value as TrackerId);
}

/** First tracker whose script pattern matches the URL. More specific patterns are listed first per tracker. */
export function matchScript(url: string): Tracker | null {
  if (!url) return null;
  for (const t of TRACKERS) if (t.scripts.some((re) => re.test(url))) return t;
  return null;
}

export function matchIframe(url: string): Tracker | null {
  if (!url) return null;
  for (const t of TRACKERS) if (t.iframes.some((re) => re.test(url))) return t;
  return null;
}

/** Tracker that owns a cookie name, if any. */
export function matchCookie(name: string): { tracker: Tracker; cookie: TrackerCookie } | null {
  for (const tracker of TRACKERS) {
    for (const cookie of tracker.cookies) {
      if (typeof cookie.name === 'string' ? cookie.name === name : cookie.name.test(name)) return { tracker, cookie };
    }
  }
  return null;
}
